import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Table } from 'reactstrap';
import Pagination from 'components/molecules/paginationTransition';
import withLoader from 'components/organisms/withLoader';
import common from 'constants/common';
import ArrowUpIcon from 'components/atoms/icons/arrowUpIcon';
import ArrowDownIcon from 'components/atoms/icons/arrowDownIcon';
import { showResult } from 'utils';
import { isTablet } from 'utils/deviceHelper';
import useUser from 'utils/useUser';
import { isCriticalOrAtRiskAlertingRole } from 'utils/accessManager/helpers';
import { addQueryParams } from 'utils/queryHelpers';
import ActivityRow from './activityRow';
import Sorting from './sorting';
import RpmCommentModal from '../../patients/patient/activity/modal';

const Activity = ({ onCallPatients, device, toggleAction }) => {
  const history = useHistory();
  const location = useLocation();
  const { roleNames } = useUser();
  const activity = useSelector((state) => state.doctor);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalActivity, setModalActivity] = useState(null);
  const { reviewed, sort, filters } = activity;
  const { readingType } = filters;
  const {
    getActivities: activitiesLoading,
    submitComment: submitCommentLoading,
    actionReview: actionReviewLoading,
    togglePatientReserved: togglePatientReservedLoading,
  } = useSelector((state) => state.loading.effects.doctor);

  const {
    doctor: {
      setActivitiesPage,
      toggleActivitiesTimeSort,
      toggleActivitiesNameSort,
      toggleActivitiesReadingTypeSort,
      submitComment,
      actionReview,
      togglePatientReserved,
    },
  } = useDispatch();

  const setPage = (pageOffset, goToPage) => {
    addQueryParams({ history, location }, { page: goToPage });
    setActivitiesPage({ pageOffset, goToPage });
  };

  const handleCommentSubmit = async (id, deviceId, values, resetForm) => {
    const postData = {
      readingId: id,
      deviceId,
      message: values.comment,
    };
    const { error } = await submitComment(postData);
    if (error) {
      showResult(error);
    }
    if (error === undefined) {
      resetForm({});
    }
  };

  const handleReviewedCheck = async (id, deviceId) => {
    const postData = {
      readingId: id,
      deviceId,
      reviewed: true,
    };
    const { error } = await actionReview(postData);
    showResult(error, common.submitSuccessTypes.reviewed);
  };

  const openModal = (data) => {
    if (!isModalOpen) {
      setModalActivity(data);
      setModalOpen(true);
    }
  };

  const columnNumber = (index) =>
    activity.page * activity.itemsPerPage + index + 1 - activity.itemsPerPage;

  return (
    <div className="activity">
      <div className="activity-header">
        <Pagination
          title=""
          page={activity.page}
          pageTotal={activity.data.length}
          itemsPerPage={activity.itemsPerPage}
          total={activity.totalCount}
          handleFirstClick={() => setPage(-(activity.page - 1))}
          handleLastClick={() =>
            setPage(0, Math.ceil(activity.totalCount / activity.itemsPerPage))
          }
          handlePrevClick={() => setPage(-1)}
          handleNextClick={() => setPage(+1)}
          loading={activitiesLoading}
          handleGoToPage={(num) => setPage(null, num)}
          isRpmPanel
        />
      </div>
      <div className="activity-body">
        {!isTablet(device) && (
          <Sorting
            action={reviewed}
            toggleAction={toggleAction}
            loading={activitiesLoading}
          />
        )}
        {withLoader(
          activitiesLoading,
          <Table
            borderless
            cellSpacing="0"
            cellPadding="0"
            className="rpm-table"
          >
            <thead>
              <tr>
                <th className="table-status-column">№</th>
                <th className="table-status-column">Status</th>
                <th className="table-time-column">
                  Time
                  <button
                    className="button-sort-arrow"
                    onClick={() => toggleActivitiesTimeSort()}
                  >
                    {sort.time ? <ArrowUpIcon /> : <ArrowDownIcon />}
                  </button>
                </th>
                <th className="table-patient-column">
                  Patient
                  <button
                    className="button-sort-arrow"
                    onClick={() => toggleActivitiesNameSort()}
                  >
                    {sort.name === 'ASC' ? <ArrowUpIcon /> : <ArrowDownIcon />}
                  </button>
                </th>
                <th className="table-reading-column">
                  Reading
                  {readingType && (
                    <button
                      className="button-sort-arrow"
                      onClick={() => toggleActivitiesReadingTypeSort()}
                    >
                      {sort.readingType === 'ASC' ? (
                        <ArrowUpIcon />
                      ) : (
                        <ArrowDownIcon />
                      )}
                    </button>
                  )}
                </th>
                {!isCriticalOrAtRiskAlertingRole(roleNames) && (
                  <th className="table-comment-column">Comment</th>
                )}
                {!isCriticalOrAtRiskAlertingRole(roleNames) && !reviewed && (
                  <th className="table-reviewed-column">Reviewed</th>
                )}
              </tr>
            </thead>
            <tbody>
              {activity.data.map((activityRow, index) => (
                <ActivityRow
                  key={`${activityRow.id}-${index}`}
                  columnNumber={columnNumber(index)}
                  activity={activityRow}
                  reviewed={reviewed}
                  handleCommentSubmit={handleCommentSubmit}
                  handleReviewedCheck={handleReviewedCheck}
                  setReviewedLoading={actionReviewLoading}
                  togglePatientReserved={togglePatientReserved}
                  isOnCall={
                    onCallPatients.includes(activityRow.patient.guid) ||
                    activityRow.patient.isOnCall
                  }
                  submitCommentLoading={
                    submitCommentLoading ||
                    actionReviewLoading ||
                    togglePatientReservedLoading
                  }
                  disabledModalButton={isModalOpen}
                  openModal={openModal}
                />
              ))}
            </tbody>
          </Table>,
        )}
      </div>
      {isModalOpen && (
        <RpmCommentModal
          isOpen={isModalOpen}
          closeModal={() => setModalOpen(false)}
          activityProps={modalActivity}
        />
      )}
    </div>
  );
};

Activity.propTypes = {
  onCallPatients: PropTypes.array,
  device: PropTypes.string,
  toggleAction: PropTypes.func.isRequired,
};

export default Activity;
